import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, MessageCircle, Instagram, Sparkles } from "lucide-react";
import { IMAGES, PHONE_TEL, WHATSAPP, INSTAGRAM_URL } from "@/data/site";

const ease = [0.22, 1, 0.36, 1];
const Line = ({ children, delay, className = "" }) => (
  <span className="line-mask">
    <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay, ease }} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden bg-grad-blush grain" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(230,0,126,0.18),transparent_60%)] blur-2xl" />
      <div className="absolute bottom-0 -left-48 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(139,0,79,0.14),transparent_60%)] blur-2xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="inline-flex items-center gap-2 card-soft rounded-full px-4 py-2 text-xs font-medium text-[#8b004f]">
            <Sparkles size={14} className="text-[#e6007e]" /> Esenyurt / İstanbul · Premium Güzellik Salonu
          </motion.div>

          <h1 className="mt-8 font-bold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-[#1a1118]" data-testid="hero-title">
            <Line delay={0.45}>Diva Nail</Line>
            <Line delay={0.6} className="text-gradient pb-2">Beauty</Line>
          </h1>

          <Line delay={0.8}>
            <p className="mt-6 text-xl sm:text-2xl font-semibold text-[#8b004f] tracking-tight" data-testid="hero-slogan">Kendinin En Güzel Hâli</p>
          </Line>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1 }} className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-zinc-600">
            Protez tırnak, manikür, pedikür, ipek kirpik ve cilt bakımı hizmetlerinde profesyonel dokunuşlarla güzelliğinizi ön plana çıkarıyoruz.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.15 }} className="mt-10 flex flex-wrap gap-3">
            <a href={PHONE_TEL} data-testid="hero-call-button" className="btn-grad-dark inline-flex items-center gap-2 text-white font-semibold px-7 py-4 rounded-full">
              <Phone size={18} /> Hemen Ara
            </a>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" className="btn-grad-light inline-flex items-center gap-2 text-white font-semibold px-7 py-4 rounded-full">
              <MessageCircle size={18} /> WhatsApp'tan Ulaş
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-instagram-button" className="btn-outline inline-flex items-center gap-2 text-[#1a1118] font-semibold px-7 py-4 rounded-full">
              <Instagram size={18} className="text-[#e6007e]" /> Instagram'da İncele
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.5 }} className="mt-14 flex items-center gap-8 text-sm text-zinc-500">
            {[["5", "Uzman Hizmet"], ["%100", "Hijyen"], ["★ 5.0", "Memnuniyet"]].map(([v, l]) => (
              <div key={l}><span className="block text-2xl font-bold text-gradient">{v}</span><span>{l}</span></div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.92, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.3, delay: 0.5, ease }} className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div onMouseMove={onMove} onMouseLeave={onLeave} style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative mx-auto max-w-sm lg:max-w-none">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-grad-pink opacity-30 blur-2xl" />
            <div className="relative rounded-[2.2rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(139,0,79,0.45)] aspect-[3/4]">
              <motion.img style={{ y: imgY, scale: 1.15 }} src={IMAGES.hero} alt="Diva Nail Beauty Esenyurt protez tırnak – soft pembe tırnaklar" className="w-full h-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2a081a]/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
                <div><p className="text-xs tracking-[0.2em] uppercase text-pink-200">Signature</p><p className="text-lg font-semibold">Soft Pink Almond</p></div>
                <Instagram size={22} />
              </div>
            </div>
            <motion.div style={{ translateZ: 60 }} className="float absolute -left-6 top-10 card-soft rounded-2xl px-4 py-3 text-sm hidden sm:block">
              <p className="font-semibold text-[#1a1118]">Protez Tırnak</p><p className="text-xs text-zinc-500">Esenyurt'un favorisi</p>
            </motion.div>
            <motion.div style={{ translateZ: 80 }} className="float absolute -right-4 bottom-24 card-soft rounded-2xl px-4 py-3 text-sm hidden sm:block" >
              <p className="font-semibold text-[#1a1118]">İpek Kirpik</p><p className="text-xs text-zinc-500">Etkileyici bakışlar</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
